// CalcGlobe smart-search dictionary.
//
// Maps abbreviations, synonyms and common typos to:
//   • Unit-converter aliases  (category + unit key in /calculators/UnitConverterFull.js)
//   • Calculator keywords     (calc id in /data/slugs.js)
//
// All keys are normalised (lower-case, no diacritics, no spaces) at lookup time
// by /lib/smartSearch.js — so authors here just need readable terms.

// ─────────────────────────────────────────────────────────────────────────────
// 1) UNIT ALIASES  →  { category, key }
// ─────────────────────────────────────────────────────────────────────────────
export const UNIT_ALIASES = {
  // ── Length ──
  ft: { c: 'length', k: 'ft' }, feet: { c: 'length', k: 'ft' }, foot: { c: 'length', k: 'ft' },
  mm: { c: 'length', k: 'mm' }, millimetre: { c: 'length', k: 'mm' }, millimeter: { c: 'length', k: 'mm' }, millimetres: { c: 'length', k: 'mm' }, millimeters: { c: 'length', k: 'mm' },
  cm: { c: 'length', k: 'cm' }, centimetre: { c: 'length', k: 'cm' }, centimeter: { c: 'length', k: 'cm' }, centimetres: { c: 'length', k: 'cm' }, centimeters: { c: 'length', k: 'cm' },
  m: { c: 'length', k: 'm' }, metre: { c: 'length', k: 'm' }, meter: { c: 'length', k: 'm' }, metres: { c: 'length', k: 'm' }, meters: { c: 'length', k: 'm' },
  km: { c: 'length', k: 'km' }, kilometre: { c: 'length', k: 'km' }, kilometer: { c: 'length', k: 'km' }, kilometres: { c: 'length', k: 'km' }, kilometers: { c: 'length', k: 'km' },
  in: { c: 'length', k: 'in' }, inch: { c: 'length', k: 'in' }, inches: { c: 'length', k: 'in' },
  yd: { c: 'length', k: 'yd' }, yard: { c: 'length', k: 'yd' }, yards: { c: 'length', k: 'yd' },
  mi: { c: 'length', k: 'mi' }, mile: { c: 'length', k: 'mi' }, miles: { c: 'length', k: 'mi' },
  nmi: { c: 'length', k: 'nmi' }, nauticalmile: { c: 'length', k: 'nmi' },
  um: { c: 'length', k: 'um' }, μm: { c: 'length', k: 'um' }, micrometre: { c: 'length', k: 'um' }, micrometer: { c: 'length', k: 'um' },
  nm_len: { c: 'length', k: 'nm' }, nanometre: { c: 'length', k: 'nm' }, nanometer: { c: 'length', k: 'nm' },

  // ── Mass / Weight ──
  kg: { c: 'mass', k: 'kg' }, kilogram: { c: 'mass', k: 'kg' }, kilograms: { c: 'mass', k: 'kg' },
  g: { c: 'mass', k: 'g' }, gram: { c: 'mass', k: 'g' }, grams: { c: 'mass', k: 'g' },
  mg: { c: 'mass', k: 'mg' }, milligram: { c: 'mass', k: 'mg' }, milligrams: { c: 'mass', k: 'mg' },
  ug: { c: 'mass', k: 'ug' }, microgram: { c: 'mass', k: 'ug' }, micrograms: { c: 'mass', k: 'ug' },
  lb: { c: 'mass', k: 'lb' }, lbs: { c: 'mass', k: 'lb' }, pound: { c: 'mass', k: 'lb' }, pounds: { c: 'mass', k: 'lb' },
  oz: { c: 'mass', k: 'oz' }, ounce: { c: 'mass', k: 'oz' }, ounces: { c: 'mass', k: 'oz' },
  st: { c: 'mass', k: 'stone' }, stone: { c: 'mass', k: 'stone' }, stones: { c: 'mass', k: 'stone' },
  tonne: { c: 'mass', k: 'tonne' }, tonnes: { c: 'mass', k: 'tonne' }, ton: { c: 'mass', k: 'uston' }, tons: { c: 'mass', k: 'uston' },

  // ── Temperature ──
  c: { c: 'temperature', k: 'C' }, celsius: { c: 'temperature', k: 'C' }, centigrade: { c: 'temperature', k: 'C' }, '°c': { c: 'temperature', k: 'C' },
  f: { c: 'temperature', k: 'F' }, fahrenheit: { c: 'temperature', k: 'F' }, farenheit: { c: 'temperature', k: 'F' }, '°f': { c: 'temperature', k: 'F' },
  k: { c: 'temperature', k: 'K' }, kelvin: { c: 'temperature', k: 'K' },

  // ── Volume ──
  l: { c: 'volume', k: 'L' }, ltr: { c: 'volume', k: 'L' }, litre: { c: 'volume', k: 'L' }, liter: { c: 'volume', k: 'L' }, litres: { c: 'volume', k: 'L' }, liters: { c: 'volume', k: 'L' },
  ml: { c: 'volume', k: 'mL' }, millilitre: { c: 'volume', k: 'mL' }, milliliter: { c: 'volume', k: 'mL' }, millilitres: { c: 'volume', k: 'mL' },
  gal: { c: 'volume', k: 'usgal' }, gallon: { c: 'volume', k: 'usgal' }, gallons: { c: 'volume', k: 'usgal' },
  ukgal: { c: 'volume', k: 'ukgal' }, ukgallon: { c: 'volume', k: 'ukgal' },
  pt: { c: 'volume', k: 'uspt' }, pint: { c: 'volume', k: 'uspt' }, pints: { c: 'volume', k: 'uspt' },
  qt: { c: 'volume', k: 'usqt' }, quart: { c: 'volume', k: 'usqt' }, quarts: { c: 'volume', k: 'usqt' },
  cup: { c: 'volume', k: 'uscup' }, cups: { c: 'volume', k: 'uscup' },
  floz: { c: 'volume', k: 'usfloz' }, fluidounce: { c: 'volume', k: 'usfloz' },
  m3: { c: 'volume', k: 'm3' }, cubicmetre: { c: 'volume', k: 'm3' }, cubicmeter: { c: 'volume', k: 'm3' },
  ft3: { c: 'volume', k: 'ft3' }, cubicfoot: { c: 'volume', k: 'ft3' }, cubicfeet: { c: 'volume', k: 'ft3' },
  in3: { c: 'volume', k: 'in3' }, cubicinch: { c: 'volume', k: 'in3' },
  tbsp: { c: 'volume', k: 'ustbsp' }, tablespoon: { c: 'volume', k: 'ustbsp' },
  tsp: { c: 'volume', k: 'ustsp' }, teaspoon: { c: 'volume', k: 'ustsp' },

  // ── Speed ──
  mph: { c: 'speed', k: 'mph' },
  kph: { c: 'speed', k: 'kmh' }, kmh: { c: 'speed', k: 'kmh' }, 'km/h': { c: 'speed', k: 'kmh' },
  'm/s': { c: 'speed', k: 'ms' }, mps: { c: 'speed', k: 'ms' },
  knot: { c: 'speed', k: 'kn' }, knots: { c: 'speed', k: 'kn' },
  fps: { c: 'speed', k: 'fts' }, 'ft/s': { c: 'speed', k: 'fts' },

  // ── Area ──
  m2: { c: 'area', k: 'm2' }, 'm²': { c: 'area', k: 'm2' }, sqm: { c: 'area', k: 'm2' }, squaremetre: { c: 'area', k: 'm2' }, squaremeter: { c: 'area', k: 'm2' },
  sqft: { c: 'area', k: 'ft2' }, ft2: { c: 'area', k: 'ft2' }, squarefoot: { c: 'area', k: 'ft2' }, squarefeet: { c: 'area', k: 'ft2' },
  sqin: { c: 'area', k: 'in2' }, in2: { c: 'area', k: 'in2' }, squareinch: { c: 'area', k: 'in2' },
  acre: { c: 'area', k: 'acre' }, acres: { c: 'area', k: 'acre' },
  hectare: { c: 'area', k: 'ha' }, hectares: { c: 'area', k: 'ha' }, ha: { c: 'area', k: 'ha' },
  km2: { c: 'area', k: 'km2' }, 'km²': { c: 'area', k: 'km2' }, sqkm: { c: 'area', k: 'km2' },
  sqmile: { c: 'area', k: 'mi2' }, mi2: { c: 'area', k: 'mi2' },
  sqyd: { c: 'area', k: 'yd2' }, yd2: { c: 'area', k: 'yd2' },

  // ── Data / Storage ──
  bit: { c: 'data', k: 'bit' }, byte: { c: 'data', k: 'B' }, bytes: { c: 'data', k: 'B' },
  kb: { c: 'data', k: 'KB' }, kilobyte: { c: 'data', k: 'KB' },
  mb: { c: 'data', k: 'MB' }, megabyte: { c: 'data', k: 'MB' },
  gb: { c: 'data', k: 'GB' }, gigabyte: { c: 'data', k: 'GB' },
  tb: { c: 'data', k: 'TB' }, terabyte: { c: 'data', k: 'TB' },
  pb: { c: 'data', k: 'PB' }, petabyte: { c: 'data', k: 'PB' },
  kib: { c: 'data', k: 'KiB' }, mib: { c: 'data', k: 'MiB' }, gib: { c: 'data', k: 'GiB' },

  // ── Time ──
  sec: { c: 'time', k: 's' }, second: { c: 'time', k: 's' }, seconds: { c: 'time', k: 's' },
  min: { c: 'time', k: 'min' }, minute: { c: 'time', k: 'min' }, minutes: { c: 'time', k: 'min' },
  hr: { c: 'time', k: 'hr' }, hour: { c: 'time', k: 'hr' }, hours: { c: 'time', k: 'hr' },
  day: { c: 'time', k: 'day' }, days: { c: 'time', k: 'day' },
  wk: { c: 'time', k: 'wk' }, week: { c: 'time', k: 'wk' }, weeks: { c: 'time', k: 'wk' },
  mo: { c: 'time', k: 'mo' }, month: { c: 'time', k: 'mo' }, months: { c: 'time', k: 'mo' },
  yr: { c: 'time', k: 'yr' }, year: { c: 'time', k: 'yr' }, years: { c: 'time', k: 'yr' },
  ms: { c: 'time', k: 'ms' }, millisecond: { c: 'time', k: 'ms' }, milliseconds: { c: 'time', k: 'ms' },
  microsecond: { c: 'time', k: 'us' }, nanosecond: { c: 'time', k: 'ns' },

  // ── Pressure ──
  psi: { c: 'pressure', k: 'psi' },
  bar: { c: 'pressure', k: 'bar' },
  pa: { c: 'pressure', k: 'Pa' }, pascal: { c: 'pressure', k: 'Pa' },
  kpa: { c: 'pressure', k: 'kPa' }, kilopascal: { c: 'pressure', k: 'kPa' },
  mpa: { c: 'pressure', k: 'MPa' }, megapascal: { c: 'pressure', k: 'MPa' },
  atm: { c: 'pressure', k: 'atm' }, atmosphere: { c: 'pressure', k: 'atm' },
  torr: { c: 'pressure', k: 'torr' }, mmhg: { c: 'pressure', k: 'torr' },

  // ── Energy ──
  j: { c: 'energy', k: 'J' }, joule: { c: 'energy', k: 'J' }, joules: { c: 'energy', k: 'J' },
  kj: { c: 'energy', k: 'kJ' }, kilojoule: { c: 'energy', k: 'kJ' },
  mj: { c: 'energy', k: 'MJ' }, megajoule: { c: 'energy', k: 'MJ' },
  cal: { c: 'energy', k: 'cal' }, calorie: { c: 'energy', k: 'cal' },
  kcal: { c: 'energy', k: 'kcal' }, kilocalorie: { c: 'energy', k: 'kcal' },
  wh: { c: 'energy', k: 'Wh' }, watthour: { c: 'energy', k: 'Wh' },
  kwh: { c: 'energy', k: 'kWh' }, kilowatthour: { c: 'energy', k: 'kWh' },
  ev: { c: 'energy', k: 'eV' }, electronvolt: { c: 'energy', k: 'eV' },
  btu: { c: 'energy', k: 'BTU' },

  // ── Power ──
  w: { c: 'power', k: 'W' }, watt: { c: 'power', k: 'W' }, watts: { c: 'power', k: 'W' },
  kw: { c: 'power', k: 'kW' }, kilowatt: { c: 'power', k: 'kW' },
  mw: { c: 'power', k: 'MW' }, megawatt: { c: 'power', k: 'MW' },
  hp: { c: 'power', k: 'hpi' }, horsepower: { c: 'power', k: 'hpi' },
  cv: { c: 'power', k: 'hpm' }, ps: { c: 'power', k: 'hpm' },

  // ── Force ──
  n: { c: 'force', k: 'N' }, newton: { c: 'force', k: 'N' }, newtons: { c: 'force', k: 'N' },
  kn: { c: 'force', k: 'kN' }, kilonewton: { c: 'force', k: 'kN' },
  lbf: { c: 'force', k: 'lbf' }, poundforce: { c: 'force', k: 'lbf' },
  kgf: { c: 'force', k: 'kgf' }, kilogramforce: { c: 'force', k: 'kgf' },
  dyne: { c: 'force', k: 'dyn' },

  // ── Angle ──
  deg: { c: 'angle', k: 'deg' }, degree: { c: 'angle', k: 'deg' }, degrees: { c: 'angle', k: 'deg' },
  rad: { c: 'angle', k: 'rad' }, radian: { c: 'angle', k: 'rad' }, radians: { c: 'angle', k: 'rad' },
  grad: { c: 'angle', k: 'grad' }, gradian: { c: 'angle', k: 'grad' },
  arcmin: { c: 'angle', k: 'arcmin' }, arcsec: { c: 'angle', k: 'arcsec' },

  // ── Fuel ──
  mpg: { c: 'fuel', k: 'mpgus' }, 'l/100km': { c: 'fuel', k: 'l100' },
  'km/l': { c: 'fuel', k: 'kml' }, kpl: { c: 'fuel', k: 'kml' },

  // ── Torque ──
  nm: { c: 'torque', k: 'Nm' }, newtonmetre: { c: 'torque', k: 'Nm' }, newtonmeter: { c: 'torque', k: 'Nm' },
  'ft-lb': { c: 'torque', k: 'lbfft' }, footpound: { c: 'torque', k: 'lbfft' }, ftlb: { c: 'torque', k: 'lbfft' }, 'lb-ft': { c: 'torque', k: 'lbfft' },
  'in-lb': { c: 'torque', k: 'lbfin' }, inchpound: { c: 'torque', k: 'lbfin' }, inlb: { c: 'torque', k: 'lbfin' },
  kgm: { c: 'torque', k: 'kgfm' }, 'kgf·m': { c: 'torque', k: 'kgfm' },

  // ── Voltage ──
  v: { c: 'voltage', k: 'V' }, volt: { c: 'voltage', k: 'V' }, volts: { c: 'voltage', k: 'V' },
  mv: { c: 'voltage', k: 'mV' }, millivolt: { c: 'voltage', k: 'mV' },
  kv: { c: 'voltage', k: 'kV' }, kilovolt: { c: 'voltage', k: 'kV' },

  // ── Current ──
  amp: { c: 'current', k: 'A' }, amps: { c: 'current', k: 'A' }, ampere: { c: 'current', k: 'A' },
  ma: { c: 'current', k: 'mA' }, milliamp: { c: 'current', k: 'mA' }, milliampere: { c: 'current', k: 'mA' },
  ka: { c: 'current', k: 'kA' }, kiloampere: { c: 'current', k: 'kA' },

  // ── Resistance ──
  ohm: { c: 'resistance', k: 'ohm' }, ohms: { c: 'resistance', k: 'ohm' }, 'ω': { c: 'resistance', k: 'ohm' },
  kohm: { c: 'resistance', k: 'kohm' }, kilohm: { c: 'resistance', k: 'kohm' },
  mohm: { c: 'resistance', k: 'Mohm' }, megaohm: { c: 'resistance', k: 'Mohm' },

  // ── Density ──
  'kg/m3': { c: 'density', k: 'kg/m3' }, 'kg/m³': { c: 'density', k: 'kg/m3' },
  'g/cm3': { c: 'density', k: 'g/cm3' }, 'g/cm³': { c: 'density', k: 'g/cm3' },
  'g/ml': { c: 'density', k: 'g/mL' }, 'kg/l': { c: 'density', k: 'kg/L' },
  'lb/ft3': { c: 'density', k: 'lb/ft3' }, 'lb/ft³': { c: 'density', k: 'lb/ft3' },

  // ── Currency ──
  usd: { c: 'currency', k: 'USD' }, dollar: { c: 'currency', k: 'USD' }, dollars: { c: 'currency', k: 'USD' },
  eur: { c: 'currency', k: 'EUR' }, euro: { c: 'currency', k: 'EUR' }, euros: { c: 'currency', k: 'EUR' },
  gbp: { c: 'currency', k: 'GBP' }, sterling: { c: 'currency', k: 'GBP' }, britishpound: { c: 'currency', k: 'GBP' },
  inr: { c: 'currency', k: 'INR' }, rupee: { c: 'currency', k: 'INR' }, rupees: { c: 'currency', k: 'INR' }, indianrupee: { c: 'currency', k: 'INR' },
  aud: { c: 'currency', k: 'AUD' }, australiandollar: { c: 'currency', k: 'AUD' },
  cad: { c: 'currency', k: 'CAD' }, canadiandollar: { c: 'currency', k: 'CAD' },
  aed: { c: 'currency', k: 'AED' }, dirham: { c: 'currency', k: 'AED' }, uaedirham: { c: 'currency', k: 'AED' },
  jpy: { c: 'currency', k: 'JPY' }, yen: { c: 'currency', k: 'JPY' }, japaneseyen: { c: 'currency', k: 'JPY' },
  cny: { c: 'currency', k: 'CNY' }, yuan: { c: 'currency', k: 'CNY' }, renminbi: { c: 'currency', k: 'CNY' },
  sgd: { c: 'currency', k: 'SGD' }, singaporedollar: { c: 'currency', k: 'SGD' },
  chf: { c: 'currency', k: 'CHF' }, swissfranc: { c: 'currency', k: 'CHF' }, franc: { c: 'currency', k: 'CHF' },
  hkd: { c: 'currency', k: 'HKD' }, nzd: { c: 'currency', k: 'NZD' },
  sek: { c: 'currency', k: 'SEK' }, nok: { c: 'currency', k: 'NOK' }, dkk: { c: 'currency', k: 'DKK' },
  mxn: { c: 'currency', k: 'MXN' }, brl: { c: 'currency', k: 'BRL' }, zar: { c: 'currency', k: 'ZAR' },
  krw: { c: 'currency', k: 'KRW' }, thb: { c: 'currency', k: 'THB' }, myr: { c: 'currency', k: 'MYR' },
  idr: { c: 'currency', k: 'IDR' }, php: { c: 'currency', k: 'PHP' }, pkr: { c: 'currency', k: 'PKR' },
  try: { c: 'currency', k: 'TRY' },
  // ── Middle East currencies ──
  sar: { c: 'currency', k: 'SAR' }, saudiriyal: { c: 'currency', k: 'SAR' }, riyal: { c: 'currency', k: 'SAR' }, riyals: { c: 'currency', k: 'SAR' },
  qar: { c: 'currency', k: 'QAR' }, qataririyal: { c: 'currency', k: 'QAR' },
  kwd: { c: 'currency', k: 'KWD' }, kuwaitidinar: { c: 'currency', k: 'KWD' }, dinar: { c: 'currency', k: 'KWD' }, dinars: { c: 'currency', k: 'KWD' },
  bhd: { c: 'currency', k: 'BHD' }, bahrainidinar: { c: 'currency', k: 'BHD' },
  omr: { c: 'currency', k: 'OMR' }, omanirial: { c: 'currency', k: 'OMR' }, rial: { c: 'currency', k: 'OMR' },
  iqd: { c: 'currency', k: 'IQD' }, iraqidinar: { c: 'currency', k: 'IQD' },
  jod: { c: 'currency', k: 'JOD' }, jordaniandinar: { c: 'currency', k: 'JOD' },
  lbp: { c: 'currency', k: 'LBP' }, lebanesepound: { c: 'currency', k: 'LBP' },
  yer: { c: 'currency', k: 'YER' }, yemenirial: { c: 'currency', k: 'YER' },
  ils: { c: 'currency', k: 'ILS' }, shekel: { c: 'currency', k: 'ILS' }, israelishekel: { c: 'currency', k: 'ILS' },
  // ── Other widely-searched currencies ──
  bdt: { c: 'currency', k: 'BDT' }, taka: { c: 'currency', k: 'BDT' },
  lkr: { c: 'currency', k: 'LKR' }, srilankanrupee: { c: 'currency', k: 'LKR' },
  npr: { c: 'currency', k: 'NPR' }, nepalirupee: { c: 'currency', k: 'NPR' },
  vnd: { c: 'currency', k: 'VND' }, dong: { c: 'currency', k: 'VND' },
  ngn: { c: 'currency', k: 'NGN' }, naira: { c: 'currency', k: 'NGN' },
  egp: { c: 'currency', k: 'EGP' }, egyptianpound: { c: 'currency', k: 'EGP' },
  kes: { c: 'currency', k: 'KES' }, ghs: { c: 'currency', k: 'GHS' }, cedi: { c: 'currency', k: 'GHS' },
  mad: { c: 'currency', k: 'MAD' }, moroccandirham: { c: 'currency', k: 'MAD' },
  rub: { c: 'currency', k: 'RUB' }, ruble: { c: 'currency', k: 'RUB' }, rouble: { c: 'currency', k: 'RUB' },
  uah: { c: 'currency', k: 'UAH' }, hryvnia: { c: 'currency', k: 'UAH' },
  pln: { c: 'currency', k: 'PLN' }, zloty: { c: 'currency', k: 'PLN' },
  twd: { c: 'currency', k: 'TWD' }, taiwandollar: { c: 'currency', k: 'TWD' },
  // Pound is currency by default when contextually currency; the parser disambiguates via "to" partner.
  pound_ccy: { c: 'currency', k: 'GBP' },
};

// ─────────────────────────────────────────────────────────────────────────────
// 2) CALCULATOR KEYWORDS  →  calc id (see /data/slugs.js)
// ─────────────────────────────────────────────────────────────────────────────
export const CALC_KEYWORDS = {
  // Finance
  emi: 'emi', loan: 'emi', homeloan: 'emi', carloan: 'emi', personalloan: 'emi', equatedmonthlyinstalment: 'emi', equatedmonthlyinstallment: 'emi',
  sip: 'sip', systematicinvestmentplan: 'sip', mutualfund: 'sip', monthlyinvestment: 'sip',
  gst: 'gst', vat: 'gst', tax: 'gst', goodsandservicestax: 'gst', valueaddedtax: 'gst',
  mortgage: 'mortgage', houseloan: 'mortgage', propertyloan: 'mortgage',
  compoundinterest: 'compound-interest', interest: 'compound-interest', investmentgrowth: 'compound-interest', fd: 'compound-interest', fixeddeposit: 'compound-interest',
  salary: 'salary', takehome: 'salary', netsalary: 'salary', incometax: 'salary', pay: 'salary',
  discount: 'discount', saleprice: 'discount', percentoff: 'discount',
  profit: 'profit-loss', loss: 'profit-loss', margin: 'profit-loss', markup: 'profit-loss', breakeven: 'profit-loss',
  roi: 'roi', returnoninvestment: 'roi', investmentreturn: 'roi',
  tip: 'tip', billsplit: 'tip', splitbill: 'tip',
  ppf: 'ppf', publicprovidentfund: 'ppf',
  inflation: 'inflation', purchasingpower: 'inflation', costofliving: 'inflation',

  // Construction
  concrete: 'concrete', cement: 'concrete', slab: 'concrete', foundation: 'concrete', m20: 'concrete', m15: 'concrete', m25: 'concrete',
  paint: 'paint', painting: 'paint', wallarea: 'paint', roompaint: 'paint',
  tile: 'tile', tiles: 'tile', floortiles: 'tile', flooringtiles: 'tile',
  brick: 'brick', bricks: 'brick', mortar: 'brick', wall: 'brick',
  roof: 'roof', roofing: 'roof', pitch: 'roof', rafter: 'roof',
  flooring: 'flooring', planks: 'flooring', laminate: 'flooring',
  rebar: 'rebar', steelbar: 'rebar', reinforcement: 'rebar', barbending: 'rebar',
  stair: 'staircase', stairs: 'staircase', staircase: 'staircase', steps: 'staircase', rise: 'staircase', run: 'staircase',
  sand: 'sand-aggregate', aggregate: 'sand-aggregate', gravel: 'sand-aggregate',
  roomarea: 'room-area', roomsize: 'room-area', roomvolume: 'room-area', airconditioning: 'room-area', btus: 'room-area',

  // Health
  calorie: 'calorie', calories: 'calorie', tdee: 'calorie', bmr: 'calorie', diet: 'calorie', weightloss: 'calorie', dailycalories: 'calorie',
  macro: 'macro', macros: 'macro', protein: 'macro', carbs: 'macro', macronutrient: 'macro',
  '1rm': 'one-rep-max', onerepmax: 'one-rep-max', benchpress: 'one-rep-max', squat: 'one-rep-max', deadlift: 'one-rep-max', maxweight: 'one-rep-max',
  running: 'running-pace', pace: 'running-pace', marathon: 'running-pace', '5k': 'running-pace', '10k': 'running-pace', halfmarathon: 'running-pace',
  water: 'water-intake', hydration: 'water-intake', waterintake: 'water-intake', drinkwater: 'water-intake',
  bodyfat: 'body-fat', 'bf%': 'body-fat', navymethod: 'body-fat', bodycomposition: 'body-fat',
  pregnancy: 'pregnancy', duedate: 'pregnancy', edd: 'pregnancy', lmp: 'pregnancy', gestationalage: 'pregnancy',
  vo2: 'vo2max', vo2max: 'vo2max', cardiofitness: 'vo2max', coopertest: 'vo2max',
  heartrate: 'heart-rate', hrzone: 'heart-rate', maxheartrate: 'heart-rate', trainingzone: 'heart-rate', bpm: 'heart-rate',
  sleep: 'sleep-cycle', sleepcycle: 'sleep-cycle', wakeup: 'sleep-cycle', bedtime: 'sleep-cycle',
  bmi: 'unit-converter',
  bodymassindex: 'unit-converter', overweight: 'unit-converter', obese: 'unit-converter', underweight: 'unit-converter',

  // Medical Reference
  needle: 'needle', gauge: 'needle', needlegauge: 'needle', injectionneedle: 'needle', syringeneedle: 'needle',
  cannula: 'iv-cannula', iv: 'iv-cannula', intravenous: 'iv-cannula', ivline: 'iv-cannula', peripheral: 'iv-cannula', venflon: 'iv-cannula', jelco: 'iv-cannula', ivcannula: 'iv-cannula',
  catheter: 'catheter', foley: 'catheter', urinarycatheter: 'catheter', french: 'catheter', fr: 'catheter',
  suture: 'suture', stitches: 'suture', vicryl: 'suture', prolene: 'suture', monocryl: 'suture', absorbable: 'suture',
  ettube: 'et-tube', endotracheal: 'et-tube', intubation: 'et-tube', airway: 'et-tube',
  bloodpressure: 'bp-reference', bp: 'bp-reference', systolic: 'bp-reference', diastolic: 'bp-reference', hypertension: 'bp-reference',
  drugdose: 'drug-dosage', drugdosage: 'drug-dosage', 'mg/kg': 'drug-dosage', paediatricdose: 'drug-dosage', paracetamoldose: 'drug-dosage',

  // Engineering Reference
  oring: 'oring', 'o-ring': 'oring', as568: 'oring', iso3601: 'oring', seal: 'oring', sealing: 'oring',
  bolt: 'bolt', nut: 'bolt', screw: 'bolt', m8: 'bolt', m10: 'bolt', m12: 'bolt', thread: 'bolt', spanner: 'bolt',
  pipe: 'pipe', tube: 'pipe', nps: 'pipe', dn: 'pipe', schedule40: 'pipe', schedule80: 'pipe', pipesize: 'pipe',
  wiregauge: 'wire-gauge', awg: 'wire-gauge', swg: 'wire-gauge', cablesize: 'wire-gauge', wiresize: 'wire-gauge', mm2elec: 'wire-gauge', ampacity: 'wire-gauge',
  sheetmetal: 'sheet-metal', gaugethickness: 'sheet-metal', galvanized: 'sheet-metal', steelsheet: 'sheet-metal',
  bearing: 'bearing', ballbearing: 'bearing', '6204': 'bearing', '6205': 'bearing', skf: 'bearing', nsk: 'bearing',
  drillbit: 'drill-bit', drillsize: 'drill-bit', drillspeed: 'drill-bit', cuttingspeed: 'drill-bit',

  // Science
  ph: 'ph', acid: 'ph', base: 'ph', buffer: 'ph', titration: 'ph', hendersonhasselbalch: 'ph',
  ohmslaw: 'electrical-circuits', vir: 'electrical-circuits',
  molarmass: 'molar-mass', molecularweight: 'molar-mass', molecularformula: 'molar-mass', h2o: 'molar-mass', h2so4: 'molar-mass',
  quadratic: 'eq-solver', quadraticformula: 'eq-solver', ax2: 'eq-solver', roots: 'eq-solver', discriminant: 'eq-solver',
  matrix: 'matrix', determinant: 'matrix', inversematrix: 'matrix', eigenvalue: 'matrix',
  derivative: 'derivative', differentiation: 'derivative', 'dy/dx': 'derivative',
  integral: 'integral', integration: 'integral', areaundercurve: 'integral', definiteintegral: 'integral',
  halflife: 'nuclear', radioactivedecay: 'nuclear', decayconstant: 'nuclear', nuclear: 'nuclear',
  idealgas: 'gas-laws', boyleslaw: 'gas-laws', charleslaw: 'gas-laws', 'pv=nrt': 'gas-laws',
  projectile: 'kinematics', trajectory: 'kinematics', suvat: 'kinematics', kinematics: 'kinematics', velocity: 'kinematics', acceleration: 'kinematics',
  complexnumber: 'complex', primefactorisation: 'prime', primefactorization: 'prime',
  baseconverter: 'base', basenumberconverter: 'base', binaryconverter: 'base',
};

// ─────────────────────────────────────────────────────────────────────────────
// 3) COMMON TYPO / SPELLING NORMALISATIONS
// ─────────────────────────────────────────────────────────────────────────────
export const SPELLING_FIX = {
  celcius: 'celsius',
  farenheit: 'fahrenheit',
  pharenheit: 'fahrenheit',
  kelvins: 'kelvin',
  fahrenhiet: 'fahrenheit',
  liter: 'litre',
  liters: 'litres',
  meter: 'metre',
  meters: 'metres',
  kilometer: 'kilometre',
  kilometers: 'kilometres',
  millimeter: 'millimetre',
  millimeters: 'millimetres',
  centimeter: 'centimetre',
  centimeters: 'centimetres',
};

// ─────────────────────────────────────────────────────────────────────────────
// 4) QUICK SEARCH CHIPS — shown below the search bar
// ─────────────────────────────────────────────────────────────────────────────
export const QUICK_CHIPS = [
  'ft to mm', 'kg to lbs', '°C to °F', 'USD to INR', 'ltr to gallon', 'mph to kph',
  'cm to inches', 'EMI', 'SIP', 'GST', 'BMI', 'IV Cannula', 'O-Ring', 'Concrete',
];

// ─────────────────────────────────────────────────────────────────────────────
// 5) PLACEHOLDER ROTATION
// ─────────────────────────────────────────────────────────────────────────────
export const PLACEHOLDER_HINTS = [
  'ft to mm', 'kg to lbs', 'USD to INR', 'EMI calculator', 'IV cannula sizes', 'concrete calculator',
];
